/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { InfoOutlineIcon, CopyIcon } from "@chakra-ui/icons";
import { Box, Text, Stack, Button, FormControl, FormLabel, Input, HStack, useToast, Flex } from "@chakra-ui/react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from "@chakra-ui/react";
import { ModalCloseButton, Divider, useDisclosure, useClipboard } from "@chakra-ui/react";
import { BiCopy } from "react-icons/bi";

import Loader from "../../../utils/Loader";
import { callApiWithToken } from "../../../utils/http_common";

import ".././styles.css";

export default function Integration({ data }) {
  const [language, setLanguage] = useState("");
  const [webhookData, setWebhookData] = useState({});
  const [loading, setLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);

  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { onCopy, setValue, hasCopied } = useClipboard(webhookData.secret);
  const { onCopy: onUsernameCopy } = useClipboard(username);
  const { onCopy: onPasswordCopy } = useClipboard(password);
  const userDetails = useSelector((state) => state?.login?.userDetails);
  const canUpdateSettings = userDetails?.permissions?.includes("settings.update");

  const url = import.meta.env.VITE_NODE_ENV === "Sandbox" ? webhookData.url || "" : webhookData.prdUrl || "";
  useEffect(() => {
    getCustomerData();
    getDefaultBrowserLanguage();
  }, []);

  const updateSettings = async () => {
    const payload = {
      impersonatedOrg: "all",
      language,
      webhookUrl,
      redirectUrl,
      key: import.meta.env.VITE_NODE_ENV === "Sandbox" ? "webhook.prdUrl" : "webhook.url",
    };

    if (!validateForm()) {
      setUpdateLoading(true);
      await callApiWithToken.post("/organization/update", payload);
      toast({
        title: "Updated Successfully..",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setUpdateLoading(false);
    }
  };
  const getCustomerData = async () => {
    setLoading(true);
    const { orgResponse = {}, webhookResponse = {} } = data?.data || {};
    const { language, formattedName, merchantKey } = orgResponse;
    const { webhook = {}, redirectUrl = "" } = webhookResponse;
    setWebhookData(webhook);
    setValue(webhook.secret);
    setLanguage(language);
    setLoading(false);
    setRedirectUrl(redirectUrl);
    setWebhookUrl(webhook?.url);
    setUsername(formattedName);
    setPassword(merchantKey);
  };

  const getDefaultBrowserLanguage = () => {
    /* Call in case default language is not set. This picks up browser language and uses it as default */
    const browserLangComplete = navigator.language || navigator.userLanguage;
    const browserLang = browserLangComplete.split("-")[0];

    if (browserLang && typeof browserLang === "string" && browserLang.toLowerCase() === "en") {
      setLanguage("english");
    }
  };

  const validateForm = () => {
    let isError = false;

    return isError;
  };

  const handleUrlChange = (event) => {
    setWebhookUrl(event.target.value);
  };

  const handleRedirectUrlChange = (event) => {
    setRedirectUrl(event.target.value);
  };

  return loading ? (
    <Loader />
  ) : (
    <Box py={5} fontFamily={"DM Sans"}>
      <Stack spacing={4}>
        <Box bg="white" borderRadius={10} p={6} boxShadow="sm">
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            API Integration Settings
          </Text>
          <Stack spacing={4}>
            <Stack direction={"column"} justifyContent="space-between" flexGrow={1} borderRadius={10} maxWidth={"full"} width={"full"}>
              <Stack w="100%" gap={"5"}>
                <FormControl size="sm">
                  <FormLabel htmlFor="text" fontWeight="medium">
                    Webhook URL
                  </FormLabel>
                  <Input
                    value={webhookUrl || url}
                    width={"100%"}
                    variant="outline"
                    placeholder="Enter webhook url"
                    onChange={handleUrlChange}
                    borderRadius="md"
                    _focus={{ borderColor: "#1A4FD6", boxShadow: "0 0 0 1px #1A4FD6" }}
                    isDisabled={!canUpdateSettings}
                  />
                </FormControl>
                <FormControl size="sm">
                  <FormLabel htmlFor="text" fontWeight="medium">
                    Redirect URL
                  </FormLabel>
                  <Input
                    value={redirectUrl}
                    width={"100%"}
                    variant="outline"
                    placeholder="Enter Redirect url"
                    onChange={handleRedirectUrlChange}
                    borderRadius="md"
                    _focus={{ borderColor: "#1A4FD6", boxShadow: "0 0 0 1px #1A4FD6" }}
                    isDisabled={!canUpdateSettings}
                  />
                  <Box display={"flex"} justifyContent={"end"} width={"100%"} mt={6}>
                    {canUpdateSettings && (
                      <Button
                        isLoading={updateLoading}
                        variant={"primary"}
                        bg={"#1A4FD6"}
                        _hover={{ bg: "#366cf5" }}
                        color="white"
                        onClick={updateSettings}
                        size="md"
                        width={"122px"}
                        height={"46px"}
                        rounded={"10px"}
                        fontWeight="medium"
                        boxShadow="sm"
                      >
                        Update
                      </Button>
                    )}
                  </Box>
                </FormControl>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        <Box bg="white" p={6} borderRadius={10} boxShadow="sm">
          <Flex w={"100%"} justifyContent={"space-between"} alignItems={"center"}>
            <Flex alignItems="center">
              <Text fontWeight={"semibold"}>Webhook Secret Key</Text>
              <Popover placement="top">
                <PopoverTrigger>
                  <InfoOutlineIcon ml={2} color="gray.500" cursor="pointer" />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody>This key is used to verify webhook requests from our service.</PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
            <Button
              color="#1A4FD6"
              border={"1px"}
              borderColor={"#1A4FD6"}
              _hover={{ bg: "#1A4FD6", textColor: "white" }}
              onClick={onOpen}
              rounded={"10px"}
              width={"122px"}
              height={"46px"}
              fontWeight="medium"
              leftIcon={<CopyIcon />}
            >
              Show Key
            </Button>
          </Flex>
        </Box>

        <Box bg="white" p={6} borderRadius={10} boxShadow="sm">
          <Text fontSize="lg" fontWeight="semibold" mb={4}>
            API Authentication
          </Text>
          <Flex w={"100%"} justifyContent={{ md: "flex-start" }} alignItems={{ md: "flex-start" }} direction={{ md: "column" }} gap={5}>
            <Flex w={"100%"} justifyContent={"flex-start"} alignItems={"center"} flexWrap="wrap">
              <Text fontWeight={"medium"} width="120px" marginRight={"10px"}>
                Username
              </Text>
              <Button
                display={"flex"}
                justifyContent={"space-between"}
                width={"100%"}
                maxWidth={"500px"}
                variant="outline"
                marginRight={"10px"}
                rightIcon={<CopyIcon color="#1A4FD6" />}
                onClick={onUsernameCopy}
                borderRadius="md"
                height="40px"
                bg="gray.50"
              >
                <Text fontWeight={"normal"} isTruncated>
                  {username}
                </Text>
              </Button>
              <Popover placement="right">
                <PopoverTrigger>
                  <InfoOutlineIcon style={{ marginBottom: "6px", fontSize: "18px" }} color="gray.500" cursor="pointer" />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody>It's your unique user name to create the authentication token for using TransFi API.</PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
            <Flex w={"100%"} justifyContent={"flex-start"} alignItems={"center"} flexWrap="wrap">
              <Text fontWeight={"medium"} width="120px" marginRight={"10px"}>
                Password
              </Text>

              <Button
                display={"flex"}
                justifyContent={"space-between"}
                width={"100%"}
                maxWidth={"500px"}
                variant="outline"
                marginRight={"10px"}
                onClick={onPasswordCopy}
                rightIcon={<CopyIcon color="#1A4FD6" />}
                borderRadius="md"
                height="40px"
                bg="gray.50"
              >
                <Text fontWeight={"normal"} isTruncated>
                  {password}
                </Text>
              </Button>

              <Popover placement="right">
                <PopoverTrigger>
                  <InfoOutlineIcon style={{ marginBottom: "6px", fontSize: "18px" }} color="gray.500" cursor="pointer" />
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverBody>It's your unique secret to create the authentication token for using TransFi API.</PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
          </Flex>
        </Box>
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Webhook Secret Key</ModalHeader>
          <ModalCloseButton top={3} />
          <Divider />
          <ModalBody>
            <FormControl>
              <FormLabel>Secret Key</FormLabel>
              <Input value={webhookData.secret} isReadOnly />
            </FormControl>
          </ModalBody>
          <Divider />
          <ModalFooter>
            <HStack gap="5">
              <Button
                leftIcon={<BiCopy />}
                variant={"primary"}
                bg={"#1A4FD6"}
                _hover={{ bg: "#366cf5" }}
                color="white"
                onClick={onCopy}
                size="md"
                width={"122px"}
                height={"46px"}
                rounded={"10px"}
              >
                {hasCopied ? "Copied" : "Copy"}
              </Button>
              <Button
                onClick={onClose}
                color="#1A4FD6"
                border={"1px"}
                borderColor={"#1A4FD6"}
                rounded={"10px"}
                _hover={{ bg: "#366cf5", textColor: "white" }}
                width={"122px"}
                height={"46px"}
              >
                Exit
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
